/**
 * Global auth header for the backend file/PDF APIs.
 *
 * PDF blobs, save-markups, combine, upload, source-folder, and file
 * list/delete/rename are fetched via raw fetch() from ~two dozen call
 * sites across the app. Rather than thread an Authorization header through
 * every one (and risk missing one — which would break that path the
 * moment the backend enforces auth), we install a single fetch wrapper
 * that adds the same credential apiFetch uses to requests hitting those
 * routes. It only ADDS an Authorization header (when absent) on matching
 * backend URLs; everything else passes straight through.
 */
import { getBackendToken } from './apiFetch';
import { BACKEND_URL } from './config';
import { stripRegionsFromSaveBody } from '../components/shared/regionMarkup';

// File/PDF routes the backend gates with requireAuth.
const FILE_API_RE = /\/api\/(files|pdf|source-folder|upload|delete)(\/|\?|$)/;

let installed = false;

export function installFileAuthInterceptor() {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;
  const orig = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    // Only string URLs to our backend's file routes; everything else
    // (Request objects, external URLs, non-file APIs) is untouched.
    const isFileApi =
      typeof input === 'string' &&
      (input.startsWith(BACKEND_URL) || input.startsWith('/api/')) &&
      FILE_API_RE.test(input);
    if (!isFileApi) return orig(input, init);

    // A region is never written to a PDF (app/regions-as-markups.md). Every
    // save path filters them; this catches one that forgot, before the
    // server's own refusal does.
    if (input.includes('/api/pdf/save-markups') && typeof init.body === 'string') {
      const { body, stripped } = stripRegionsFromSaveBody(init.body);
      if (stripped) {
        console.warn(`[regions] ${stripped} region markup(s) kept out of a PDF save`);
        init = { ...init, body };
      }
      // A snip goes into the FILE at print resolution, re-rendered from
      // its source drawing (shared/printPictures): every save route sends
      // through here, so none can send the storage-sized picture by mistake.
      // Loaded on demand: the module reaches pdf.js, which the sign-in
      // pages never need.
      try {
        const { upgradeSaveBodyPictures } = await import('../components/shared/stampSharpen');
        const upgraded = await upgradeSaveBodyPictures(init.body);
        if (upgraded !== init.body) init = { ...init, body: upgraded };
      } catch (e) { console.warn('[stampSharpen] print pictures skipped:', e?.message || e); }
    }
    const headers = { ...(init.headers || {}) };
    const hasAuth = Object.keys(headers).some(k => k.toLowerCase() === 'authorization');
    if (!hasAuth) {
      const token = await getBackendToken().catch(() => null);
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    const res = await orig(input, { ...init, headers });
    // A DIGITALLY SIGNED document (backend services/pdfSignatures): a save
    // that keeps its content is written so the signatures stay valid; one
    // that rewrites the signed content over the file is refused with 409
    // until the person says so. Asked here, once, for every save route.
    if (res.status === 409 && input.includes('/api/pdf/save-markups') && typeof init.body === 'string') {
      let info = null;
      try { info = await res.clone().json(); } catch { info = null; }
      if (info?.code === 'signed-document') {
        const { pidlyConfirm } = await import('./dialogs');
        const go = await pidlyConfirm(
          info.certified ? info.error : `${info.error} Markups can still be added without affecting the signatures.`,
          { title: 'Signed document', confirmLabel: 'Save and invalidate signatures', confirmColor: 'danger' },
        );
        if (!go) return res;
        let body = init.body;
        try { body = JSON.stringify({ ...JSON.parse(init.body), breakSignatures: true }); } catch { /* keep */ }
        return orig(input, { ...init, body, headers });
      }
    }
    return res;
  };
}
